import { computed, ref } from 'vue';

// COMPOSABLES
import { toast } from '@flash-global66/g-toast';
import useLoader from '@/composables/useLoader';
import { usePinFlow } from '../../composables/usePinFlow';
import { providePinRestore } from '../../composables/usePinRestoreProvider';

// API
import { pinAuthService } from '../../services/pinAuth.service';

// TYPES
import { BusinessFlow } from '@/shared/otp';
import { PinRestoreStep } from '../../types/pin.type';
import type {
  PinEmits,
  PinRestoreNavigation,
  UsePinRestoreReturn,
} from '../../types/pin.type';

// ERRORS
import { ErrorsPin } from '@/shared/common/errors';
import { ErrorsJumio } from '@/shared/jumio/errors/jumio.error';
import { ErrorsPinRestore } from '../../errors/pinRestore.error';
import { getReasonMessage } from '@/shared/common/utils/errorMessages.util';

export function usePinRestore(
  emit: PinEmits,
  navigation: PinRestoreNavigation
): UsePinRestoreReturn {
  const routesGoToHome = ['/transaction', '/exchange'];

  const { step, currentPin } = providePinRestore();
  const verificationAttempt = ref(0);

  const { hidePin } = usePinFlow();
  const { showGLoader } = useLoader();

  const isVerificationOpen = computed(
    () => step.value === PinRestoreStep.VERIFICATION
  );

  /** Cierra el mini-app tras el reseteo exitoso, volviendo a movimientos si venía de una ruta de pago. */
  function finishRestore() {
    if (routesGoToHome.includes(navigation.getCurrentPath())) {
      navigation.goToMovements();
    }
    hidePin();
    emit('close');
  }

  function handleRightHeader() {
    finishRestore();
  }

  /** Actualiza el PIN con el workflow verificado; si falla, deja al usuario repetir la verificación. */
  async function onVerified(workflowExecutionId: string) {
    showGLoader(true);

    const result = await pinAuthService
      .updatePinByWorkflow({
        pin: currentPin.value,
        businessFlow: BusinessFlow.RESET_PIN_B2B,
        workflowExecutionId,
      })
      .finally(() => showGLoader(false));

    result.fold(
      (failure) => {
        verificationAttempt.value += 1;
        toast.error(
          getReasonMessage({
            failure,
            errorMap: { ...ErrorsJumio, ...ErrorsPin, ...ErrorsPinRestore },
            defaultMessage:
              'No pudimos actualizar tu PIN. Vuelve a verificar tu identidad.',
          })
        );
      },
      () => {
        currentPin.value = '';
        finishRestore();
        toast.success('Pin actualizado');
      }
    );
  }

  /** Vuelve a pedir el PIN nuevo sin llamar al PUT. */
  function onCancelVerification() {
    currentPin.value = '';
    step.value = PinRestoreStep.PIN;
  }

  return {
    step,
    isVerificationOpen,
    verificationAttempt,
    handleRightHeader,
    onVerified,
    onCancelVerification,
    PinRestoreStep,
  };
}
